import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, X } from 'lucide-react';
import { getAppText, useLanguageStore } from '../../stores/languageStore';

export default function UpdateNotification() {
  const { appLanguage } = useLanguageStore();
  const t = getAppText(appLanguage);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const lastHashRef = useRef(localStorage.getItem('sw_index_hash'));
  const lastSwVersionRef = useRef(localStorage.getItem('sw_cache_version'));
  const isUpdatingRef = useRef(false);

  const applyUpdate = async () => {
    if (isUpdatingRef.current) return;

    isUpdatingRef.current = true;
    setIsUpdating(true);

    try {
      // Clear all caches to force fresh assets after the new version is detected.
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      }
    } catch (error) {
      console.error('Failed to clear caches during update:', error);
    } finally {
      localStorage.removeItem('sw_index_hash');

      window.setTimeout(() => {
        window.location.reload();
      }, 250);
    }
  };

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      const { type, hash, version } = event?.data || {};

      if (type === 'CHECK_UPDATE' && hash) {
        if (!lastHashRef.current) {
          localStorage.setItem('sw_index_hash', hash);
          lastHashRef.current = hash;
        } else if (lastHashRef.current !== hash) {
          console.log('🆕 New version available, reloading now...');
          setUpdateAvailable(false);
          void applyUpdate();
        } else {
          localStorage.setItem('sw_index_hash', hash);
          lastHashRef.current = hash;
        }
      }

      if (type === 'SW_ACTIVATED') {
        console.log('✅ Service Worker activated with new version');
        setUpdateAvailable(false);

        if (version && lastSwVersionRef.current !== version && !isUpdatingRef.current) {
          localStorage.setItem('sw_cache_version', version);
          lastSwVersionRef.current = version;
          void applyUpdate();
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    const checkForUpdate = () => {
      if (navigator.serviceWorker.controller) {
        fetch('/index.html', { cache: 'no-store' })
          .catch((err) => console.error('Update check failed:', err));
      }
    };

    // Check for updates on mount, on return to the tab, and periodically.
    checkForUpdate();

    const interval = setInterval(() => {
      checkForUpdate();
    }, 300000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  const handleDismiss = () => {
    setUpdateAvailable(false);
  };

  if (!updateAvailable) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-5">
      <div className="bg-white dark:bg-neutral-800 border-2 border-primary-500 rounded-lg shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              {t.newVersionTitle}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              {t.newVersionText}
            </p>
            <div className="flex gap-2">
              <button
                onClick={applyUpdate}
                disabled={isUpdating}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 text-white text-sm font-medium rounded transition-all duration-200 flex items-center justify-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t.updating}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {t.updateNow}
                  </>
                )}
              </button>
              <button
                onClick={handleDismiss}
                disabled={isUpdating}
                aria-label={t.closeUpdate}
                className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 text-sm font-medium rounded transition-colors duration-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600 animate-pulse" />
      </div>
    </div>
  );
}
